import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { EventPhotosPreview } from '../../components/client/EventPhotosPreview';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, Users, AlertTriangle, Star, Send,
  Download, Sparkles, MessageSquare, CheckCircle2, XCircle, MapPin, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatClockTimeInEventTz } from '../../utils/timeFormat';

/**
 * Client Event Recap — executive reporting layout (mirrors ievntur.com).
 * Dark charcoal report banner + composite performance score, KPI cards,
 * AI summary, roster, ratings, engagement, photos, talent notes.
 * Actions: Download professional PDF · Email report.
 */
const scoreColor = (s) =>
  s == null ? 'text-slate-300' : s >= 75 ? 'text-emerald-400' : s >= 50 ? 'text-amber-400' : 'text-rose-400';

const KpiCard = ({ label, value, sub, accent = 'text-slate-900' }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-2xl sm:text-3xl font-extrabold leading-tight mt-1 ${accent}`}>{value ?? '—'}</p>
    {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const ClientRecapDashboard = () => {
  const navigate = useNavigate();
  const { shiftId } = useParams();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/client/recap/${shiftId}`);
      setRecap(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recap');
    } finally { setLoading(false); }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  const sendEmail = async () => {
    setEmailing(true);
    try {
      const r = await api.post(`/client/recap/${shiftId}/email`, { force: true });
      const body = r.data || {};
      if (body.sent) toast.success(`Recap emailed to ${body.to}`);
      else toast.warning(body.reason === 'no_client_email' ? 'No email on file' : 'Report queued for delivery');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    } finally { setEmailing(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/client/recap/${shiftId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const venue = (recap?.shift?.venue_name || 'event').replace(/\s+/g, '_').slice(0, 40);
      link.download = `recap-${venue}-${recap?.shift?.shift_date || ''}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (e) {
      toast.error('Could not generate PDF');
    } finally { setDownloading(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  }
  if (!recap) return null;

  const { shift, attendance, talent_roster, ratings, engagement, narratives, ai_deliverables } = recap;
  const score = recap.performance_score;
  const ai = recap.ai_insights || {};
  const showRate = attendance.show_rate != null ? attendance.show_rate
    : (attendance.scheduled > 0 ? Math.round((attendance.completed / attendance.scheduled) * 100) : 0);
  const loc = [shift.city, shift.state].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="client-recap-page">
      {/* Toolbar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Download PDF
            </Button>
            <Button size="sm" onClick={sendEmail} disabled={emailing}
              className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="email-recap-btn">
              {emailing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Email report
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Report banner (executive style) */}
        <div className="rounded-2xl bg-[#283046] px-6 py-6 flex items-start justify-between gap-4 flex-wrap" data-testid="recap-banner">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Event Recap Report</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-1.5" data-testid="recap-venue">{shift.venue_name}</h1>
            <div className="text-sm text-slate-300 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {shift.shift_date}</span>
              {loc && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {loc}</span>}
              <Badge className="bg-emerald-500/90 text-white border-0 capitalize">{shift.shift_status}</Badge>
            </div>
          </div>
          <div className="bg-[#3a4157] rounded-xl px-5 py-3 text-center shrink-0" data-testid="performance-score">
            <p className="text-[10px] font-bold uppercase tracking-wide text-blue-300">Performance Score</p>
            <p className={`text-4xl font-extrabold leading-none mt-1 ${scoreColor(score)}`}>{score ?? '—'}</p>
            <p className="text-[10px] text-slate-400 mt-1">out of 100</p>
          </div>
        </div>

        {/* AI Summary */}
        {(ai.summary || ai.status === 'generating') && (
          <div className="rounded-xl bg-violet-50 border border-violet-200 p-4" data-testid="recap-ai-summary">
            <p className="flex items-center gap-2 text-violet-700 font-bold text-sm">
              <Sparkles className="w-4 h-4" /> AI Summary
            </p>
            {ai.status === 'generating' ? (
              <p className="text-sm text-violet-900/70 mt-1.5 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating insights from your event data — refresh in a moment.
              </p>
            ) : (
              <>
                <p className="text-sm text-violet-900/90 mt-1.5 leading-relaxed">{ai.summary}</p>
                {Array.isArray(ai.findings) && ai.findings.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {ai.findings.slice(0, 6).map((f, i) => (
                      <li key={i} className="text-xs text-violet-900/80 flex items-start gap-1.5">
                        <span className={`mt-0.5 inline-block px-1.5 rounded text-[9px] font-bold uppercase ${
                          f.severity === 'high' ? 'bg-rose-100 text-rose-700'
                          : f.severity === 'medium' ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-600'}`}>{f.severity}</span>
                        {f.detail}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="recap-kpis">
          <KpiCard label="Staff Present" value={`${attendance.completed}/${attendance.scheduled}`} sub={`${showRate}% show rate`} accent="text-cyan-700" />
          <KpiCard label="Hours Worked" value={attendance.total_hours} sub="total logged" accent="text-slate-900" />
          <KpiCard label="Avg Rating" value={ratings.avg_rating ?? '—'} sub={`${ratings.count} rating(s)`} accent="text-amber-600" />
          <KpiCard label="Leads" value={engagement.leads_total} sub={`${engagement.qualified_leads_total || 0} qualified`} accent="text-emerald-700" />
          <KpiCard label="Foot Traffic" value={engagement.foot_traffic_total} sub="estimated" accent="text-violet-700" />
          <KpiCard label="No-shows" value={attendance.no_show} sub={`${attendance.late} late`} accent={attendance.no_show ? 'text-rose-600' : 'text-slate-900'} />
        </div>

        {/* Roster */}
        <section data-testid="recap-roster">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Talent Roster</h3>
            <span className="text-xs text-gray-500">{talent_roster.length} booked</span>
          </div>
          {talent_roster.length === 0 ? (
            <Card><CardContent className="py-6 text-center text-gray-500 text-sm">No bookings on this shift.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {talent_roster.map(t => (
                <Card key={t.booking_id} className={t.is_no_show ? 'border-rose-200 bg-rose-50/40' : ''} data-testid={`roster-${t.booking_id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    {t.profile_photo_url
                      ? <img src={t.profile_photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                      : <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center"><Users className="w-5 h-5 text-cyan-600" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm truncate">{t.name}</p>
                        {t.is_shift_lead && <Badge className="bg-violet-500 text-white text-[10px] border-0">Lead</Badge>}
                        {t.is_ai_ambassador && <Badge className="bg-fuchsia-500 text-white text-[10px] border-0">AI</Badge>}
                        {t.is_no_show
                          ? <Badge className="bg-rose-500 text-white text-[10px] border-0">No-show</Badge>
                          : t.is_late
                            ? <Badge className="bg-amber-500 text-white text-[10px] border-0">Late {t.late_minutes}m</Badge>
                            : <Badge className="bg-emerald-500 text-white text-[10px] border-0">{t.status}</Badge>}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">
                        In {formatClockTimeInEventTz(t.clock_in_time, t.timezone, t.state)} · Out {formatClockTimeInEventTz(t.clock_out_time, t.timezone, t.state)} · {t.hours_worked}h
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Ratings + Engagement */}
        <div className="grid md:grid-cols-2 gap-4">
          <section data-testid="recap-ratings">
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2">Ratings</h3>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Average rating</span>
                  <span className="text-2xl font-bold text-amber-500 flex items-center gap-1">
                    <Star className="w-5 h-5 fill-amber-400" /> {ratings.avg_rating ?? '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Ratings submitted</span>
                  <span className="font-medium">{ratings.count}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Would rehire</span>
                  <span className="font-medium">{ratings.rehire_yes}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1"><XCircle className="w-3.5 h-3.5 text-rose-500" /> Would not rehire</span>
                  <span className="font-medium">{ratings.rehire_no}</span>
                </div>
              </CardContent>
            </Card>
          </section>

          <section data-testid="recap-engagement">
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2">Engagement</h3>
            <Card>
              <CardContent className="p-4 grid grid-cols-2 gap-3">
                <KpiCard label="Leads" value={engagement.leads_total} accent="text-cyan-700" />
                <KpiCard label="Qualified" value={engagement.qualified_leads_total} accent="text-emerald-700" />
                <KpiCard label="Foot Traffic" value={engagement.foot_traffic_total} accent="text-cyan-700" />
                <KpiCard label="Surveys" value={engagement.submitted_count} accent="text-violet-700" />
              </CardContent>
            </Card>
          </section>
        </div>

        {/* Narratives */}
        {narratives.length > 0 && (
          <section data-testid="recap-narratives">
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2">Talent recap notes</h3>
            <div className="space-y-2">
              {narratives.map((n, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-500" /> {n.talent}</p>
                      {n.overall_rating != null && <Badge variant="outline" className="text-[10px]">★ {n.overall_rating}</Badge>}
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.text}</p>
                    {n.issues_reported && (
                      <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">{n.issues_reported}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Photos */}
        <section data-testid="recap-photos">
          <EventPhotosPreview shiftId={shiftId} />
        </section>

        {/* AI deliverables */}
        {ai_deliverables.length > 0 && (
          <section data-testid="recap-ai-deliverables">
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-fuchsia-500" /> AI Campaign Deliverables
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {ai_deliverables.map((a, i) => (
                <Card key={i} className="border-fuchsia-100">
                  <CardContent className="p-4">
                    <p className="font-semibold text-sm">{a.name}</p>
                    {a.demo_video_url && (
                      <a href={a.demo_video_url} target="_blank" rel="noreferrer"
                         className="text-xs text-fuchsia-600 hover:underline">View demo →</a>
                    )}
                    {a.package_tiers?.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {a.package_tiers.length} package tier{a.package_tiers.length === 1 ? '' : 's'} delivered
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ClientRecapDashboard;
